import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatDateOnly } from '../utils/timezone';

export default function PlaceMyReferrals() {
  const { user, loading: authLoading } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [placing, setPlacing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [referralsRes, slotsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/user/my-referrals`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/my-tree-slots`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!referralsRes.ok || !slotsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const referralsData = await referralsRes.json();
      const slotsData = await slotsRes.json();

      setReferrals(referralsData.referrals || []);
      setAvailableSlots(slotsData.available_slots || []);

      if (!slotsData.user_placed) {
        setError('You must be placed in the genealogy tree before you can place referrals. Contact admin.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceReferral = async () => {
    if (!selectedReferral || !selectedSlot) {
      setError('Please select both a referral and a slot');
      return;
    }

    if (!confirming) {
      setConfirming(true);
      return;
    }

    setPlacing(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/mlm/tree/place-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedReferral.id,
          parent_node_id: selectedSlot.parent_node_id,
          leg_position: selectedSlot.leg_position,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to place referral');
      }

      alert(`Successfully placed ${selectedReferral.first_name} ${selectedReferral.last_name} on Leg ${selectedSlot.leg_position}!`);
      setSelectedReferral(null);
      setSelectedSlot(null);
      setConfirming(false);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
        <div className="max-w-container-max mx-auto">
          <div className="flex items-center justify-center py-12">
            <p style={{ color: '#b8a68f' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-container-max mx-auto">
        {/* Header */}
        <div className="mb-8 border-b pb-6" style={{ borderColor: '#c9a961' }}>
          <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
            Place My Referrals
          </h1>
          <p className="text-body-md" style={{ color: '#b8a68f' }}>
            Position your referred members in the genealogy tree
          </p>
        </div>

        {error && (
          <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p style={{ color: '#c9a961' }}>{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Referrals List */}
          <div className="lg:col-span-2 border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
              My Referrals Ready for Placement
            </h2>

            {referrals.length === 0 ? (
              <div className="text-center py-8">
                <p style={{ color: '#b8a68f' }}>No referrals ready for placement</p>
                <p className="text-body-sm mt-2" style={{ color: '#999' }}>
                  Referrals must be verified or pre-processed before they can be placed
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrals.map((referral) => (
                  <button
                    key={referral.id}
                    onClick={() => {
                      setSelectedReferral(referral);
                      setSelectedSlot(null);
                      setConfirming(false);
                    }}
                    className="w-full text-left p-4 rounded-lg border transition-all"
                    style={{
                      backgroundColor: selectedReferral?.id === referral.id ? '#1a3a2a' : 'transparent',
                      borderColor: selectedReferral?.id === referral.id ? '#c9a961' : '#444',
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium" style={{ color: '#c9a961' }}>
                          {referral.first_name} {referral.last_name}
                        </p>
                        <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                          {referral.email}
                        </p>
                        <p className="text-body-xs mt-1" style={{ color: '#999' }}>
                          {referral.pre_processed
                            ? 'Pre-processed (no package purchase yet)'
                            : `Package: ${referral.package_name_en} • Verified: ${formatDateOnly(referral.verified_at)}`}
                        </p>
                      </div>
                      <div
                        className="px-3 py-1 rounded text-body-xs font-medium"
                        style={{
                          backgroundColor: referral.pre_processed ? '#5a4a1a' : '#2a5a3a',
                          color: referral.pre_processed ? '#c9a961' : '#7bc76f',
                        }}
                      >
                        {referral.pre_processed ? 'Pre-processed' : 'Ready'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Slot Selection */}
          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
              Available Slots
            </h2>

            {availableSlots.length === 0 ? (
              <div className="text-center py-8">
                <p style={{ color: '#b8a68f' }}>No available slots</p>
                <p className="text-body-sm mt-2" style={{ color: '#999' }}>
                  All your tree legs are occupied
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableSlots.map((slot) => (
                  <button
                    key={`leg_${slot.leg_position}`}
                    onClick={() => {
                      setSelectedSlot(slot);
                      setConfirming(false);
                    }}
                    className="w-full p-4 rounded-lg border transition-all text-left"
                    style={{
                      backgroundColor: selectedSlot?.leg_position === slot.leg_position ? '#2a5a3a' : 'transparent',
                      borderColor: selectedSlot?.leg_position === slot.leg_position ? '#7bc76f' : '#444',
                    }}
                  >
                    <p className="font-medium" style={{ color: selectedSlot?.leg_position === slot.leg_position ? '#7bc76f' : '#c9a961' }}>
                      Leg {slot.leg_position}
                    </p>
                    <p className="text-body-xs" style={{ color: '#b8a68f' }}>
                      Position under your node
                    </p>
                  </button>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            {selectedReferral && selectedSlot && (
              <div className="mt-6 space-y-2">
                {!confirming ? (
                  <button
                    onClick={handlePlaceReferral}
                    disabled={placing}
                    className="w-full px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black"
                    style={{ backgroundColor: '#c9a961' }}
                  >
                    {placing ? 'Placing...' : 'Place Referral'}
                  </button>
                ) : (
                  <>
                    <div className="bg-yellow-900/30 border border-yellow-600 rounded p-3 mb-2">
                      <p className="text-body-xs" style={{ color: '#c9a961' }}>
                        Confirm: Place <strong>{selectedReferral.first_name} {selectedReferral.last_name}</strong> on <strong>Leg {selectedSlot.leg_position}</strong>?
                      </p>
                    </div>
                    <button
                      onClick={handlePlaceReferral}
                      disabled={placing}
                      className="w-full px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black text-body-sm"
                      style={{ backgroundColor: '#7bc76f' }}
                    >
                      {placing ? 'Confirming...' : '✓ Confirm'}
                    </button>
                    <button
                      onClick={() => setConfirming(false)}
                      disabled={placing}
                      className="w-full px-4 py-2 rounded-lg font-medium border text-body-sm"
                      style={{ borderColor: '#c9a961', color: '#c9a961' }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
